import React, { forwardRef } from "react";
import { FormControl, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";

type Option = { [key: string]: any };

type Props = {
  title: string;
  options: Option[];
  value: string | number;
  onChange: (event: SelectChangeEvent<string | number>) => void;
  required?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  labelKey: string;
  valueKey: string;
};

const primaryColor = "#292744";

/** Custom styled form control for consistent UI across app */
const CustomFormControl = styled(FormControl)({
  minWidth: "100%",

  // Label style
  "& .MuiInputLabel-root": {
    color: primaryColor,
    fontWeight: 500,
    fontSize: 15,
    background: "#f7f8fa",
    padding: "0 4px",
  },

  // Required asterisk color
  "& .MuiInputLabel-root.Mui-required .MuiInputLabel-asterisk": {
    color: "#d32f2f", // red color for required asterisk
  },

  // Outlined input styling
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },

  // Selected text inside dropdown
  "& .MuiSelect-select": {
    padding: "14px",
    fontSize: 'var(--font-size-medium)',
    fontFamily: 'var(--font-family-main)',
    color: "#222",
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
  },
});


/** ------------------------------------------------------------------
 *  MAIN COMPONENT
 * ------------------------------------------------------------------ */
const DropDownText = forwardRef<HTMLInputElement, Props>(
  ({ title, options, value, onChange, required, style, disabled, labelKey, valueKey }, ref) => {

    // Ensure options is always an array
    const safeOptions = Array.isArray(options) ? options : [];

    return (
      <CustomFormControl variant="outlined" required={required} style={style}>
        <InputLabel
          style={{ color: '#555', fontFamily: 'var(--font-family-main)', fontSize: 'var(--font-size-medium)' }}
          required={required}
        >
          {title}
        </InputLabel>
        <Select
          label={title}
          value={value}
          onChange={onChange}
          disabled={disabled}
          labelId={`${title.replace(" ", "-").toLowerCase()}-label`}
        >
          <MenuItem
            style={{
              fontFamily: "var(--font-family-main) !important",
              fontSize: "var(--font-size-medium) !important",
            }}
            value="">Select</MenuItem>

          {/* Render all options */}
          {safeOptions.map((option, index) => (
            <MenuItem
              key={index}
              value={option[valueKey]}
              style={{
                fontFamily: "var(--font-family-main) !important",
                fontSize: "var(--font-size-medium) !important",
              }}
            >
              <span
                style={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 265,
                }}
              >
                {option[labelKey]}
              </span>
            </MenuItem>
          ))}
        </Select>
      </CustomFormControl>
    );
  }
);

export default DropDownText;
